import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Tooltip, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from "react";
import { useFetcher, useLoaderData } from 'react-router-dom';
import { uploadFileFromObject } from "../../../api/common";
import { Box } from "../../../components/design";
import { InputWrapper } from "../../../components/inputs/input-wrappers.jsx";
import { OrderShippingStatuses } from "../../../library/constants/shipping-statuses.js";
import { getFilePath } from '../../../library/constants/urls.js';
import { formatDeliveryAddress } from '../../../library/helpers/index.js';
import { formatDate } from '../../../library/utils/intl.js';

import MultifileUpload from "../../../ui/multifile-upload.jsx";
import { ShipmentConfirmDispatchDate } from './expectedDispatchDate.jsx';
import { ShipmentExpectedDispatchDate } from './inProduction.jsx';
import styles from './product-dispatch.module.scss';
import { getAdjustedDeliveryDays } from '../../../library/utils/deliverydate.js';
import { showAlert } from '../../../components/message/alert.jsx';

const DeliveryConfirm = ({ projectId, item, onConfirm, deliveryType = null }) => {
    const address = (deliveryType === 3) ?
        <Button href={getFilePath(item.bulk_address_file.url, projectId)} icon={<DownloadOutlined />}>Download</Button> :
        <>{formatDeliveryAddress(item)}</>

    return (
        <div className={deliveryType === 3 ? styles.deliveryRowWithComment : styles.deliveryRow}>
            <div className="quantity">{item.quantity}</div>
            <div className="address">{address}</div>
            <div className="field-quantity">{item.ship_quantity ?? item.quantity}</div>
            <div className="field-expected_date">
                <Tooltip title="The date the order was planned to be shipped to Leoprinting.">
                    <span>{formatDate(item.dispatch_date)}</span>
                </Tooltip>
            </div>
            {deliveryType === 3 ? (
                <>
                    <div className="field-expected_date">{<Button href={getFilePath(item.delivery_file.url, projectId)} icon={<DownloadOutlined />}>Download</Button>}</div>
                    <div className="field-trackingurl">{item.delivery_comment}</div>
                </>
            ) : (
                <>
                    <div className="field-trackingurl"><a target="blank" href={item.tracking_url} rel="noreferrer noopener">View</a></div>
                    <div className="field-expected_date">
                        <Tooltip title="The date the shipment is expected to arrive at the delivery address.">
                            <span>{formatDate(item.expected_date)}</span>
                        </Tooltip>
                    </div>
                </>
            )}
            {item.status === 4 ? (
                <Button
                    type="primary"
                    name="action"
                    disabled
                >Proof Sent</Button>
            ) : (
                <Button
                    type="primary"
                    name="action"
                    value="confirmDelivery"
                    onClick={onConfirm}
                >Confirm</Button>
            )}

        </div>
    )
}
DeliveryConfirm.propTypes = {
    projectId: PropTypes.number.isRequired,
    item: PropTypes.object.isRequired,
    onConfirm: PropTypes.func.isRequired,
    deliveryType: PropTypes.oneOf([1, 2, 3]),
}

const InTransit = () => {
    const { productionDetails } = useLoaderData()
    const fetcher = useFetcher();
    const { errors = {} } = fetcher.data || {}
    const fileUploaderRef = useRef([])
    const inputComment = useRef('')
    const [isUploading, setIsUploading] = useState(false)
    const [confirmDeliveryId, setConfirmDeliveryId] = useState(false)
    const missingExpectedDateShipments = productionDetails.item_delivery_address.filter(({ status }) => status === OrderShippingStatuses.NEW)
    const dispachableShipments = productionDetails.item_delivery_address.filter(({ status }) => status === OrderShippingStatuses.DISPATCH_DATE_ADDED)
    const deliverableShipments = productionDetails.item_delivery_address.filter(({ status }) => [OrderShippingStatuses.DETAILS_ADDED, OrderShippingStatuses.CONFIRM].includes(status))
    const expectedDeliveryDays = getAdjustedDeliveryDays(productionDetails.item_delivery_address.find((delivery)=> delivery)?.expected_date)    
    
    useEffect(() => {
            setIsUploading(fetcher.json?.action === 'confirmDelivery')
        }, [fetcher.state, fetcher.data])

    const handleFilesUpload = async (deliveryId) => {
        if (fileUploaderRef.current.length === 0) {
            showAlert('Please select atleast one file')
            return
        }
        setIsUploading(true)
        const promises = fileUploaderRef.current.map(file => uploadFileFromObject(file))
        try {
            const fileIds = await Promise.all(promises)
            fetcher.submit({
                deliveryId,
                image_ids: fileIds,
                comment: inputComment.current,
                action: 'confirmDelivery'
            }, {
                method: 'post',
                encType: 'application/json'
            })
            closeConfirmDeliveryPopup()
        }
        catch (error) {
            message.error(error.message)
            return
        }
        finally {
            setIsUploading(false)
        }
    }

    const closeConfirmDeliveryPopup = () => {
        if (isUploading && confirmDeliveryId) return //prevent closing popup while file is being uploaded
        setConfirmDeliveryId(null)
        inputComment.current = ''
        fileUploaderRef.current = []
    };

    return (
        <div className={styles.container}>
            <h3>Task For You</h3>
            <Alert
                description={
                    expectedDeliveryDays === 0
                        ? `You have shipped the order, the delivery is expected today.`
                        : `You have shipped the order, the delivery will take ${expectedDeliveryDays} to ${expectedDeliveryDays + 1} days.`
                }
                type="info"
                showIcon
            />
            <Box header={
                <div className={productionDetails.delivery_type === 3 ? styles.deliveryRowHeadWithComment : styles.deliveryRowHead}>
                    <div>Quantity</div>
                    <div>Delivery Address</div>
                    <div>Ship Quantity</div>
                    <Tooltip title="The date the order was planned to be shipped to Leoprinting.">
                        <span>Expected Dispatch Date <InfoCircleOutlined /></span>
                    </Tooltip>
                    {productionDetails.delivery_type === 3 ? (
                        <>
                            <div>Delivery File</div>
                            <div>Comment</div>
                        </>
                    ) : (
                        <>
                            <div>Tracking URL</div>
                            <Tooltip title="The date the shipment is expected to arrive at the delivery address.">
                                <span>Expected Delivery Date <InfoCircleOutlined /></span>
                            </Tooltip>
                        </>
                    )}
                    <div></div>
                </div>
            }>
                {!!deliverableShipments && deliverableShipments.length > 0 && deliverableShipments.map((shipment) => (
                    <DeliveryConfirm
                        key={shipment.id}
                        projectId={productionDetails.project_id}
                        item={shipment}
                        deliveryType={productionDetails.delivery_type}
                        onConfirm={() => {
                            setConfirmDeliveryId(shipment.id)
                        }}
                    />
                ))}
            </Box>
            {dispachableShipments.length > 0 && (
                <>
                    <Alert description="Please provide an expected shipment details for following items" type="error" showIcon />
                    <Box header={
                        <div className={styles.deliveryRowHead}>
                            <div>Quantity</div>
                            <div>Delivery Address</div>
                            <Tooltip title="The date you plan to ship this order to Leoprinting.">
                                <span>Expected Dispatch Date <InfoCircleOutlined /></span>
                            </Tooltip>
                            <div>Ship Quantity</div>
                            <div>Tracking URL</div>
                            <Tooltip title="The date you expect the shipment to arrive at the delivery address.">
                                <span>Expected Delivery Date <InfoCircleOutlined /></span>
                            </Tooltip>
                            <div></div>
                        </div>
                    }>
                        {dispachableShipments.map((shipment) => <ShipmentConfirmDispatchDate key={shipment.id} projectId={productionDetails.project_id} itemId={productionDetails.id} item={shipment} />)}
                    </Box>
                </>
            )}
            {missingExpectedDateShipments.length > 0 && (
                <>
                    <Alert description="Please provide shipment details for following items" type="error" showIcon />
                    <ShipmentExpectedDispatchDate projectId={productionDetails.project_id} itemId={productionDetails.id} deliveryType={productionDetails.delivery_type} shipments={missingExpectedDateShipments} buttonText="Save Dates" />
                </>
            )}
            <Modal
                title="Upload Proof of Delivery"
                open={confirmDeliveryId}
                onOk={() => handleFilesUpload(confirmDeliveryId)}
                confirmLoading={isUploading}
                okText="Continue"
                onCancel={closeConfirmDeliveryPopup}
            >
                <div className={styles.uploadBox}>
                    <p>The uploaded printscreen should not contain track and trace code, country of origin and sender information</p>
                    <div className="main">
                        <div className="box--white">
                            <MultifileUpload ref={fileUploaderRef} />
                        </div>
                    </div>
                    <br />
                    <InputWrapper name="comment" label="Comment" defaultValue="" textarea allowClear errors={errors} onChange={e => inputComment.current = e.target.value} />
                </div>
            </Modal>
        </div>
    )
}
export default InTransit;
